/**
 * AdSense slot wrapper. Renders one of two things depending on the
 * `VITE_ADSENSE_ENABLED` env flag:
 *
 *   - ENABLED  → real `<ins class="adsbygoogle">` with lazy IntersectionObserver
 *                push (mirrors the legacy `REACTJS/src/app/components/Adsense.jsx`
 *                policy posture).
 *   - DISABLED → a dashed placeholder box with the same min-height/width so
 *                layout doesn't shift between dev and prod.
 *
 * ## AdSense policy compliance (DO NOT regress)
 *
 *   1. **Max 3 ad units per page** (`MAX_ADS_PER_PAGE`). A page-scoped counter
 *      is reserved on mount in tree order; later slots that miss the cap
 *      render `null`. Resets when the route path changes. Applies to BOTH
 *      modes so the dev view accurately reflects what prod will serve.
 *   2. **Visible "Advertisement" label** — `aria-label` + an "ADVERTISEMENT"
 *      caption on the placeholder.
 *   3. **Defensive sizing** — never collapse to 0×0 (policy 22) or hide via
 *      `display:none`/`visibility:hidden`/`opacity:0` (policy 23). Containers
 *      carry an explicit `min-width: 160` + `overflow: visible`.
 *   4. **Min-width gate** — fluid ads need ≥ 250px, display ads need ≥ 160px
 *      before push, so AdSense never serves into an undersized slot.
 *   5. **Lazy load** — IntersectionObserver with 200px root-margin so ads only
 *      request when scrolled within ~200px of viewport. Raises viewability,
 *      avoids loading ads for content the user never scrolls to.
 *   6. **Full-width responsive** — `data-full-width-responsive="true"` so
 *      AdSense's responsive logic kicks in.
 *   7. **Excluded surfaces** — Login/Register/ForgotPassword/Home/About/
 *      Pricing/Privacy/Contact/Messages/Livestream/EditProfile/Settings/
 *      InviteFriends/all NotFound pages/LiveTV Watch (active video) MUST NOT
 *      mount this component. Enforced at the caller level (verified during
 *      the AdSense rollout workflow).
 *   8. **Empty-state gate** — callers wrap the slot in a `!isLoading &&
 *      !isError && data.length > 0` branch so we never render an ad next to
 *      a "No results" / error message.
 *
 * Keep all callers using `<AdsenseSlot {...AdSlots.foo}/>` from `@/lib/adSlots`
 * — the slot registry is the single source of truth for IDs / formats.
 */
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { cn } from '@/lib/utils';
import type { AdSlot } from '@/lib/adSlots';
import { EVENTS, track } from '@/lib/analytics';

const ADSENSE_ENABLED =
  String(import.meta.env.VITE_ADSENSE_ENABLED ?? '').toLowerCase() === 'true';
const ADSENSE_CLIENT_ID = import.meta.env.VITE_ADSENSE_CLIENT_ID ?? '';
const ADSENSE_SCRIPT_URL = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js';

/** Approximate served height for the placeholder — close enough that the
 * layout doesn't pop when we swap the placeholder for a real ad in prod. */
const PLACEHOLDER_HEIGHT_BY_FORMAT: Record<AdSlot['format'], number> = {
  auto: 250,
  fluid: 280,
  rectangle: 250,
};

/* -------------------------------------------------------------------------- */
/* Page-level policy guard rails                                              */
/* -------------------------------------------------------------------------- */

/** Hard cap on ad units per page (AdSense policy + old REACTJS parity). */
const MAX_ADS_PER_PAGE = 3;

/** Reserved slot count for the current route. Reset on path change. */
const pageState = {
  count: 0,
  path: '',
};

/** Reserve a slot for the current path; returns true if the cap had room. */
function reserveSlot(pathname: string): boolean {
  if (pathname !== pageState.path) {
    pageState.path = pathname;
    pageState.count = 0;
  }
  if (pageState.count >= MAX_ADS_PER_PAGE) return false;
  pageState.count += 1;
  return true;
}

/** Release a reservation (called on unmount so virtualized lists don't leak). */
function releaseSlot(pathname: string): void {
  if (pathname !== pageState.path) return; // stale — already reset by a newer route
  if (pageState.count > 0) pageState.count -= 1;
}

/* -------------------------------------------------------------------------- */
/* AdSense script bootstrap (live mode only)                                  */
/* -------------------------------------------------------------------------- */

let scriptInjected = false;
let scriptScheduled = false;

/**
 * Schedule the AdSense SDK fetch AFTER the browser is idle.
 * `adsbygoogle.js` is ~90kB gzipped and isn't required for first
 * paint or LCP — the IO observer below polls `window.adsbygoogle`
 * with a built-in retry so a late-arriving SDK is fine. Using
 * `requestIdleCallback` (with `setTimeout` fallback for Safari)
 * pushes the request off the critical path so fonts, the LCP
 * image, and the entry bundle don't compete for bandwidth.
 *
 * Once any slot mounts we only need to schedule the load ONCE
 * (`scriptScheduled` guard) — subsequent slots ride the same
 * script load.
 */
function ensureAdsenseScript(): void {
  if (!ADSENSE_ENABLED || !ADSENSE_CLIENT_ID) return;
  if (scriptInjected || scriptScheduled || typeof document === 'undefined') return;
  if (document.querySelector(`script[src^="${ADSENSE_SCRIPT_URL}"]`)) {
    scriptInjected = true;
    return;
  }
  scriptScheduled = true;
  const inject = () => {
    if (scriptInjected) return;
    const s = document.createElement('script');
    s.async = true;
    s.crossOrigin = 'anonymous';
    s.src = `${ADSENSE_SCRIPT_URL}?client=${ADSENSE_CLIENT_ID}`;
    document.head.appendChild(s);
    scriptInjected = true;
  };
  const ric = (window as unknown as {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
  }).requestIdleCallback;
  if (typeof ric === 'function') {
    ric(inject, { timeout: 3000 });
  } else {
    window.setTimeout(inject, 1500);
  }
}

/* -------------------------------------------------------------------------- */
/* Component                                                                   */
/* -------------------------------------------------------------------------- */

interface AdsenseSlotProps extends AdSlot {
  /** Extra classes for the outer container (margin / max-width tweaks). */
  className?: string;
  /** Override the lazy-load delay (ms) after intersection. */
  timeout?: number;
  /** Override placeholder height when the design dictates a specific size. */
  placeholderHeight?: number;
}

export function AdsenseSlot({
  slot,
  format,
  layout,
  layoutKey,
  className,
  timeout = 300,
  placeholderHeight,
}: AdsenseSlotProps) {
  const location = useLocation();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const insRef = useRef<HTMLModElement | null>(null);
  const pushed = useRef(false);
  // Dedupe — `data-ad-status` can flip from 'unfilled' to 'filled' on a
  // refresh (e.g. when AdSense fills after the first auction attempt
  // returned no demand). We only want one AD_IMPRESSION per slot mount.
  const impressionFiredRef = useRef(false);
  /**
   * Deferred-release handle for the slot reservation. StrictMode replays
   * the mount effect (mount → cleanup → mount) in dev; we want the second
   * mount to short-circuit the cleanup so the page-cap counter doesn't
   * briefly under-count and incorrectly null-out sibling slots that
   * mount in the same render. The cleanup queues the release on a 0ms
   * timer; the next mount cancels it if it fires synchronously after.
   */
  const pendingReleaseRef = useRef<number | null>(null);

  /**
   * `reserved` is the page-cap claim — true means we hold one of the
   * MAX_ADS_PER_PAGE slots for this route. False means later-mounted slots
   * after the cap was full; we render nothing.
   */
  const [reserved, setReserved] = useState(false);
  /** Live-mode flag flipped once the IO push fires (real `<ins>` rendered). */
  const [showAd, setShowAd] = useState(false);

  // -- Slot reservation (applies to BOTH placeholder + live modes) ----------
  useEffect(() => {
    const path = location.pathname;
    // If a previous cleanup queued a release (StrictMode mount→cleanup→mount
    // or rapid re-render), cancel it and re-use the existing reservation.
    if (pendingReleaseRef.current != null) {
      window.clearTimeout(pendingReleaseRef.current);
      pendingReleaseRef.current = null;
      setReserved(true);
      return () => {
        pendingReleaseRef.current = window.setTimeout(
          () => releaseSlot(path),
          0,
        );
      };
    }
    const ok = reserveSlot(path);
    setReserved(ok);
    if (!ok) return undefined;
    return () => {
      pendingReleaseRef.current = window.setTimeout(
        () => releaseSlot(path),
        0,
      );
    };
  }, [location.pathname]);

  // -- Live-mode push pipeline ----------------------------------------------
  useEffect(() => {
    if (!ADSENSE_ENABLED || !ADSENSE_CLIENT_ID || !reserved) return;
    ensureAdsenseScript();

    pushed.current = false;
    setShowAd(false);

    const tryPush = (): void => {
      try {
        const width = containerRef.current?.offsetWidth ?? 0;
        const minWidth = format === 'fluid' ? 250 : 160;
        const w = window as unknown as { adsbygoogle?: unknown[] };
        if (width >= minWidth && w.adsbygoogle && !pushed.current) {
          setShowAd(true);
          window.setTimeout(() => {
            if (!pushed.current) {
              pushed.current = true;
              (w.adsbygoogle = w.adsbygoogle || []).push({});
            }
          }, 10);
        }
      } catch {
        /* AdSense errors must never break the page. */
      }
    };

    let observer: IntersectionObserver | null = null;
    let timer: number | null = null;
    const supportsIO =
      typeof window !== 'undefined' && 'IntersectionObserver' in window;

    if (supportsIO && containerRef.current) {
      observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting && !pushed.current) {
              timer = window.setTimeout(tryPush, timeout);
              observer?.disconnect();
              observer = null;
            }
          }
        },
        { rootMargin: '200px 0px', threshold: 0.01 },
      );
      observer.observe(containerRef.current);
    } else {
      timer = window.setTimeout(tryPush, timeout);
    }

    return () => {
      if (timer) window.clearTimeout(timer);
      if (observer) observer.disconnect();
    };
  }, [reserved, location.pathname, slot, timeout, format]);

  // -- AD_IMPRESSION (fill observer) ----------------------------------------
  // AdSense stamps the `<ins>` element with `data-ad-status="filled"` or
  // `"unfilled"` AFTER the SDK runs the auction. Only `filled` counts as
  // a real served ad — `unfilled` means Google had no demand to show
  // (no impression, no revenue, no analytics signal). We watch the attribute
  // with a MutationObserver so the event fires regardless of when the SDK
  // resolves the auction relative to React's render cycle.
  useEffect(() => {
    if (!ADSENSE_ENABLED || !ADSENSE_CLIENT_ID) return;
    if (!showAd) return;
    const el = insRef.current;
    if (!el || impressionFiredRef.current) return;

    const fireIfFilled = (): boolean => {
      if (impressionFiredRef.current) return true;
      if (el.getAttribute('data-ad-status') === 'filled') {
        impressionFiredRef.current = true;
        try {
          track(EVENTS.AD_IMPRESSION, {
            slot,
            format,
            layout: layout ?? null,
            path: location.pathname,
          });
        } catch {
          /* ignore */
        }
        return true;
      }
      return false;
    };

    if (fireIfFilled()) return;

    if (typeof MutationObserver === 'undefined') return;
    const mo = new MutationObserver(() => {
      if (fireIfFilled()) mo.disconnect();
    });
    mo.observe(el, {
      attributes: true,
      attributeFilter: ['data-ad-status'],
    });
    return () => mo.disconnect();
  }, [showAd, slot, format, layout, location.pathname]);

  // ---- Cap reached — render nothing (matches what prod will do) ----------
  if (!reserved) return null;

  // ---- Disabled / placeholder mode --------------------------------------
  if (!ADSENSE_ENABLED || !ADSENSE_CLIENT_ID) {
    const height = placeholderHeight ?? PLACEHOLDER_HEIGHT_BY_FORMAT[format];
    return (
      <div
        aria-label="Advertisement placeholder"
        className={cn(
          'my-4 flex w-full items-center justify-center rounded-md border border-dashed border-border bg-surface-2 text-xs text-muted-foreground',
          className,
        )}
        style={{ minHeight: height }}
        data-ad-placeholder
        data-slot={slot}
      >
        <div className="flex flex-col items-center gap-1">
          <span className="font-medium uppercase tracking-wide text-muted-foreground/80">
            Advertisement
          </span>
          <span className="text-[10px] tabular-nums text-muted-foreground/60">
            slot {slot} · {format}
          </span>
        </div>
      </div>
    );
  }

  // ---- Live mode ---------------------------------------------------------
  const params: Record<string, string> = {};
  if (layout) params['data-ad-layout'] = layout;
  if (layoutKey) params['data-ad-layout-key'] = layoutKey;

  return (
    <div
      ref={containerRef}
      aria-label="Advertisement"
      className={cn('my-4 w-full text-center', className)}
      style={{
        // No `minHeight` reservation in live mode (user choice 2026-06-30):
        // when Google serves a short ad — or no ad at all — a fixed
        // reserved height leaves a tall empty padded box. We accept the
        // CLS hit (0 → served height) in exchange for never showing
        // empty reserved space below a short creative. The disabled /
        // placeholder branch above still reserves height because that
        // mode IS the visible UI.
        minWidth: 160,
        // Pin to parent column. `maxWidth: 100%` + `overflow: hidden`
        // keep AdSense's `data-full-width-responsive` from blowing the
        // ad past its container into the sidebar (seen 2026-06-25 in
        // the dashboard timeline). The container is height:auto (no
        // min-height since 2026-06-30) so the box sizes to the served
        // creative — `overflow: hidden` on both axes can't clip ad
        // content vertically because there's no fixed height to
        // contain past.
        maxWidth: '100%',
        overflow: 'hidden',
        boxSizing: 'border-box',
      }}
    >
      {showAd ? (
        <ins
          ref={insRef}
          className="adsbygoogle"
          style={{ display: 'block', maxWidth: '100%', width: '100%' }}
          data-ad-format={format}
          {...params}
          data-ad-client={ADSENSE_CLIENT_ID}
          data-ad-slot={slot}
          data-full-width-responsive="true"
        />
      ) : null}
    </div>
  );
}
